import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { GET_USER } from '../../graphql/queries';
import { impersonation, type ImpersonationState } from '../../services/impersonation';

/**
 * Sticky yellow warning banner shown on every authenticated page while an
 * impersonated session is active.
 *
 * Source of truth is the id-token's `custom:impersonatedBy` claim. The claim
 * is read exactly once at mount via `useState`'s lazy initialiser — no
 * `useLocation` / `useEffect` re-read on route changes. The claim can only
 * change on sign-in / sign-out / token refresh, all of which unmount and
 * remount the banner through the parent Layout's auth transitions.
 *
 * See: .kiro/specs/admin-impersonation/design.md → Frontend —
 * `components/layout/ImpersonationBanner.tsx`.
 */
export const ImpersonationBanner: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);

  // Claim read ONCE at mount — regression guard against route-change re-reads.
  const [state] = useState<ImpersonationState | null>(() =>
    impersonation.getImpersonationState(),
  );

  // Resolve the admin's display name via `getUser(userId: adminSub)`. Apollo
  // caches the result so subsequent mounts on the same admin are free.
  const { data: adminData } = useQuery<{ getUser: { userId: string; name?: string | null; email?: string | null } | null }>(
    gql`${GET_USER}`,
    {
      variables: { userId: state?.actorAdminSub ?? '' },
      skip: !state,
      fetchPolicy: 'cache-first',
    },
  );

  if (!state) return null;

  const adminDisplay =
    adminData?.getUser?.name ||
    adminData?.getUser?.email ||
    state.actorAdminSub;

  const handleExit = () => {
    void impersonation.exitImpersonation();
  };

  return (
    <div
      role="alert"
      className="sticky top-0 z-40 bg-yellow-400 text-yellow-900 px-4 py-2 flex items-center justify-between text-sm shadow-sm"
    >
      <span className="flex items-center gap-2 min-w-0">
        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">
          {t('admin:impersonation.banner', '대리 로그인 중: 관리자 {{admin}} → {{target}}', {
            admin: adminDisplay,
            target: state.targetEmail || state.targetSub,
          })}
        </span>
      </span>
      <button
        type="button"
        onClick={handleExit}
        className="ml-4 flex-shrink-0 font-medium underline hover:no-underline"
      >
        {t('admin:impersonation.exit', '원래 계정으로 돌아가기')}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
